import type { Lang } from './index'

export const categoryNameMap: Record<Lang, Record<string, string>> = {
  zh: {},
  en: {
    base: 'Basic Shapes',
    curtain: 'Backdrop / Image',
    house: 'Floor Plan / Walls',
    camera: 'Camera',
    other: 'Other',
    mineObjs: 'My Assets',
  },
}

/**
 * Property / enum display labels used by entity getDataMeta() and enumList.
 * Keyed by the original Chinese label; the English dictionary maps to English.
 * zh is intentionally empty (identity fallback).
 */
export const propertyLabelMap: Record<Lang, Record<string, string>> = {
  zh: {},
  en: {
    // Common / base
    'id 唯一标识': 'ID',
    '提示标记': 'Label',
    '标记字号': 'Label Size',
    '是否锁定': 'Locked',
    '是否隐藏': 'Hidden',
    '数据': 'Data',
    '子对象': 'Children',
    '组名称': 'Group Name',
    '文件类型': 'File Type',
    '形状': 'Shape',
    '图案': 'Pattern',
    '图片': 'Image',
    '图片角度Y': 'Image Y Rotation',
    '缩放': 'Scale',
    '旋转角度': 'Rotation',
    '角度': 'Angle',
    '颜色': 'Color',
    '材质': 'Material',
    '高度': 'Height',
    '宽度': 'Width',
    '长度': 'Length',
    '半径': 'Radius',
    '底部半径': 'Bottom Radius',
    '顶部半径': 'Top Radius',
    '边数': 'Sides',
    '弧度': 'Arc',
    '开始角度': 'Start Angle',
    '结束角度': 'End Angle',
    '管道半径': 'Pipe Radius',
    '柱子半径': 'Pole Radius',
    '柱子颜色': 'Pole Color',
    '身高': 'Height',
    '骨骼': 'Skeleton',
    '距离地面': 'Height from Ground',
    '距离地面距离': 'Height from Ground',
    '距离地面高度': 'Height from Ground',
    '目标X轴': 'Target X',
    '目标Y轴': 'Target Y',
    '目标Z轴': 'Target Z',
    '相机Z轴': 'Camera Z',
    '广角角度': 'Field of View',
    // Position
    'x坐标': 'X Position',
    'y坐标': 'Y Position',
    'z坐标': 'Z Position',
    'Z轴': 'Z Axis',
    'y旋转角度': 'Y Rotation',
    // Wall / house
    '所属墙': 'Wall',
    '门在墙上的点的索引': 'Wall Point Index',
    '是否挂在外墙': 'On Outer Wall',
    '是否可以旋转Z轴角度': 'Allow Z Rotation',
    '是否双面': 'Double-sided',
    '是否双面可见': 'Double-sided',
    '所有点': 'Points',
    '转角类型': 'Corner Type',
    '墙体材质': 'Wall Material',
    '墙体颜色': 'Wall Color',
    '墙体高度': 'Wall Height',
    '墙体宽度': 'Wall Width',
    '墙体厚度': 'Wall Thickness',
    '隐藏墙': 'Hide Wall',
    '整个墙体属性': 'Wall Properties',
    '整个面属性': 'Surface Properties',
    '整个楼梯属性': 'Staircase Properties',
    '墙面属性': 'Wall Surface',
    '顶点属性': 'Vertex Properties',
    '顶点高度': 'Vertex Height',
    '插入顶点': 'Insert Vertex',
    '删除顶点': 'Delete Vertex',
    '从此顶点断成两截': 'Split at Vertex',
    // Door / window
    '开门方式': 'Opening Type',
    '开门方式(1内左开 2内右开 3外左开 4外右开)': 'Opening Type (1 In-Left 2 In-Right 3 Out-Left 4 Out-Right)',
    '门打开的角度': 'Door Angle',
    '左门打开角度': 'Left Door Angle',
    '右门打开角度': 'Right Door Angle',
    '左窗打开角度': 'Left Window Angle',
    '右窗打开角度': 'Right Window Angle',
    '是否有门框': 'Has Frame',
    '门框材质': 'Frame Material',
    '门框颜色': 'Frame Color',
    '玻璃框材质': 'Glass Frame Material',
    '玻璃框颜色': 'Glass Frame Color',
    '内开': 'Inward',
    '外开': 'Outward',
    // Staircase
    '台阶类型': 'Step Type',
    '独立台阶': 'Separate Steps',
    '落地台阶': 'Solid Steps',
    '楼梯材质': 'Staircase Material',
    '楼梯颜色': 'Staircase Color',
    '楼梯宽度': 'Staircase Width',
    // Room surfaces
    '是否有地板': 'Has Floor',
    '地板材质': 'Floor Material',
    '地板颜色': 'Floor Color',
    '是否有天花板': 'Has Ceiling',
    '天花板材质': 'Ceiling Material',
    '天花板颜色': 'Ceiling Color',
    '天花板是否是双面': 'Ceiling Double-sided',
    '是否有包墙': 'Has Wall Wrap',
    '包墙材质': 'Wall Wrap Material',
    '包墙颜色': 'Wall Wrap Color',
    // Sign
    '牌子背景色': 'Sign Background',
    '牌子高度': 'Sign Height',
    '牌子宽度': 'Sign Width',
    '牌子离地高度': 'Sign Ground Clearance',
    // Sections / misc
    '基本信息': 'Basic',
    '下载文件': 'Download File',
    '姿态编辑': 'Pose Edit',
    'Y字路口': 'Y Junction',
    '丁字路口': 'T Junction',
    '掉头': 'U-turn',
    '前方有红绿灯': 'Traffic Light Ahead',
    '右侧车道变窄': 'Right Lane Narrows',
    '左侧车道变窄': 'Left Lane Narrows',
    '三角形警告': 'Warning Triangle',
    '三角形双向通行': 'Two-way Traffic',
    '双向': 'Two-way',
    '右转': 'Turn Right',
    '左转': 'Turn Left',
    '圆形限速': 'Speed Limit',
    '自定义文字': 'Custom Text',
    '矩形': 'Rectangle',
    '菱形': 'Diamond',
    '三角形': 'Triangle',
    '圆形': 'Circle',
    '无': 'None',
    '无纹理': 'No Texture',
    // Corner types
    '对角，无独立墙蹲': 'Diagonal, no separate quoins',
    '切脚，独立墙蹲': 'Chamfered, separate quoins',
    '对角，独立墙蹲': 'Diagonal, separate quoins',
    '圆角，独立墙蹲': 'Rounded, separate quoins',
    // Material library
    '所有材质': 'All Materials',
    '编辑纹理': 'Edit Texture',
    // People animation names
    '垂手站立': 'Standing',
    '走路': 'Walking',
    '趴下': 'Plank',
    '跑': 'Run',
    '躺': 'Lying',
    '游泳': 'Swimming',
    '侧躺': 'Side Lying',
    '坐': 'Sitting',
    '跳跃': 'Jumping',
    '翘腿坐': 'Cross-legged Sit',
    '倚靠坐': 'Leaning Sit',
    '前倾坐': 'Leaning Forward Sit',
    '倚靠坐2': 'Leaning Sit 2',
    '蹲下': 'Squatting',
    '跪下': 'Kneeling',
    '拳击': 'Punching',
    '手撑躺': 'Propped Lying',
    '单腿俏皮站立': 'Playful One-leg Stand',
    '踢人': 'Kicking',
    '大摇大摆走': 'Strutting',
    '吊': 'Hanging',
    '托马斯': 'Thomas Flair',
    '前滚翻': 'Forward Roll',
    '射箭': 'Archery',
    '棒球击打': 'Baseball Hit',
    '街舞扫腿': 'Breakdance Sweep',
    '舞蹈舒展': 'Dance Stretch',
    '霹雳舞波浪': 'Hip-hop Wave',
    '单膝跪': 'One-knee Kneel',
    '抱着盒子': 'Carrying Box',
    '挥手': 'Waving',
    '捂脸': 'Facepalm',
    '手枪射击': 'Pistol Shooting',
    '上爬': 'Climbing',
    '握手': 'Handshake',
    // Ground texture editor labels
    '透明度': 'Opacity',
    '纹理缩放': 'Texture Scale',
    '是否有边框': 'Has Border',
    '线条颜色': 'Line Color',
    '边框宽度': 'Border Width',
    '是否有填充': 'Has Fill',
    '背景颜色': 'Background Color',
    '圆角半径': 'Corner Radius',
    '文字': 'Text',
    '文字大小': 'Font Size',
    '文字颜色': 'Text Color',
    '起点箭头': 'Start Arrow',
    '终点箭头': 'End Arrow',
    '起点箭头大小': 'Start Arrow Size',
    '终点箭头大小': 'End Arrow Size',
    // Ground texture editor elements
    '地砖': 'Tile',
    '草坪': 'Grass',
    '斑马线': 'Crosswalk',
    '警示牌': 'Sign',
    '花坛': 'Flower Bed',
    '道路': 'Road',
    '圆': 'Circle',
    '井盖': 'Manhole',
    '箭头': 'Arrow',
    '线': 'Line',
    // Line cap types
    '圆头': 'Round',
    '方块': 'Square',
    '直线箭头': 'Line Arrow',
    '三角形箭头': 'Triangle Arrow',
    '倒三角形箭头': 'Reversed Triangle Arrow',
    '圆形箭头': 'Circle Arrow',
    '菱形箭头': 'Diamond Arrow',
    // Environment editor
    '天空种类': 'Sky Type',
    '白天': 'Day',
    '清晨': 'Early Morning',
    '傍晚': 'Evening',
    '黄昏': 'Dusk',
    '夜晚（满月）': 'Night (Full Moon)',
    '夜晚（新月）': 'Night (New Moon)',
    '城市白天': 'City Day',
    '环境补充光强度': 'Ambient Light Intensity',
    '显示默认地面': 'Show Default Ground',
  },
}
